import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ErrorHandling from '../components/ErrorHandling';
import useApi from '../hooks/useApi';
import useShopifyApi from '../hooks/useShopifyApi';
import useAgreementContract from '../hooks/useAgreementContract';
import useListenSize from '../hooks/useListenSize';
import SubmitButton from '../components/SubmitButton';
import { AgreementChecklistItem } from '../components/AgreementChecklistItem';
import {
  confirmLeaseAgreement,
  confirmAonAgreement,
  confirmKplusAcknowledgement,
  confirmKplusAgreement,
} from '../redux/actions';
import { segmentEvent } from '../helpers/sendEvent';
import {
  isKplusAvailable as checkKplusAvailable,
  isKplusAcknowledgementAvailable as checkKplusAcknowledgementAvailable,
} from '../helpers/kplusPricing';

async function noopContractFetch() {
  return null;
}

const ALREADY_ENROLLED_ERROR = 'A live KP+ membership already exists for this lease';

function LeaseAgreement() {
  const { request, response: enrollResponse } = useApi();
  const [kplusEnrollError, setKplusEnrollError] = useState(null);
  const [boostEnrollError, setBoostEnrollError] = useState(null);
  const { request: shopifyRequest, response: shopifyResponse } = useShopifyApi();
  const dispatch = useDispatch();
  const history = useHistory();
  useListenSize();

  const store = useSelector(state => ({
    checkoutUid: state.loanInfo?.checkoutUid,
    payFrequency: state.loanInfo?.payFrequency,
    monthlyTotal: state.loanInfo?.checkout?.monthly_total,
    checkout: state.loanInfo?.checkout,
    featureGateLinearFlow: state.loanInfo?.featureGateLinearFlow,
    shopify: state.technical?.shopify,
    isAonFlow: state.loanInfo?.isAonFlow,
    confirmedAonAgreement: state.loanInfo?.confirmedAonAgreement,
    userId: state.loanInfo?.userId,
  }));

  const isKplusAvailable = checkKplusAvailable(store.checkout);
  const isKplusAcknowledgementAvailable = checkKplusAcknowledgementAvailable(store.checkout);

  useEffect(() => {
    segmentEvent('page', 'Lease Agreement', {
      lease_payment: store.monthlyTotal,
      pay_frequency: store.payFrequency,
    });
  }, []);

  const leaseAgreementFetcher = async uid => {
    if (store.shopify?.isShopify) {
      await shopifyRequest.leaseAgreementRequest(uid);
      if (shopifyResponse.ok) {
        return shopifyResponse.json();
      }
      history.push('/error');
      return undefined;
    }
    return request.getLeaseAgreementContract(uid);
  };

  const lease = useAgreementContract(leaseAgreementFetcher, store.checkoutUid);
  const kplusAcknowledgement = useAgreementContract(
    isKplusAcknowledgementAvailable ? request.getKplusAcknowledgement : noopContractFetch,
    store.checkoutUid,
  );
  const kplusAgreement = useAgreementContract(
    isKplusAvailable ? request.getKplusAgreement : noopContractFetch,
    store.checkoutUid,
  );
  const boost = useAgreementContract(store.isAonFlow ? request.getAonContract : noopContractFetch, store.checkoutUid);

  const [acknowledged, setAcknowledged] = useState({
    lease: false,
    kplusAcknowledgement: false,
    kplusAgreement: false,
    boost: false,
  });

  const rows = useMemo(() => {
    const list = [{ key: 'lease', title: 'Katapult Lease Agreement', ...lease }];
    if (isKplusAcknowledgementAvailable) {
      list.push({ key: 'kplusAcknowledgement', title: 'Katapult Protection+ Acknowledgment', ...kplusAcknowledgement });
    }
    if (isKplusAvailable) {
      list.push({ key: 'kplusAgreement', title: 'Katapult Protection+ Agreement', ...kplusAgreement });
    }
    if (store.isAonFlow) {
      list.push({ key: 'boost', title: 'Katapult Boost Agreement', ...boost });
    }
    return list.map((row, i) => ({ ...row, index: i + 1 }));
  }, [
    isKplusAvailable,
    isKplusAcknowledgementAvailable,
    store.isAonFlow,
    lease,
    kplusAcknowledgement,
    kplusAgreement,
    boost,
  ]);

  const firstError = rows.find(row => row.error)?.error || null;
  const acknowledgedCount = rows.filter(row => acknowledged[row.key]).length;
  const allAcknowledged = rows.length > 0 && acknowledgedCount === rows.length;
  const progressPercent = rows.length ? Math.round((acknowledgedCount / rows.length) * 100) : 0;
  const remainingCount = rows.length - acknowledgedCount;

  useEffect(() => {
    if (allAcknowledged) {
      segmentEvent('track', 'Lease Agreement Reviewed', {
        lease_payment: store.monthlyTotal,
        pay_frequency: store.payFrequency,
      });
    }
  }, [allAcknowledged]);

  const handleRowAcknowledge = async (row, value) => {
    if (value && (row.loading || !row.contractHtml)) {
      return;
    }
    if (row.key === 'kplusAgreement' && value) {
      setKplusEnrollError(null);
      const enrollResult = await request.enrollKplus(store.checkoutUid, true);
      const isAlreadyEnrolled = enrollResponse?.status === 409 && enrollResult?.error === ALREADY_ENROLLED_ERROR;
      if (!enrollResponse?.ok && !isAlreadyEnrolled) {
        setKplusEnrollError('We could not enroll you in Katapult Protection+. Please try again.');
        return;
      }
      dispatch(confirmKplusAgreement(true));
    }
    if (row.key === 'boost' && value && !store.confirmedAonAgreement) {
      setBoostEnrollError(null);
      const enrollResult = await request.updateAonEnrollmentStatus(store.userId, {
        opted_for_aon_subscription: true,
      });
      if (!enrollResult?.ok) {
        setBoostEnrollError('We could not enroll you in Katapult Boost. Please try again.');
        return;
      }
      dispatch(confirmAonAgreement(true));
    }
    setAcknowledged(prev => ({ ...prev, [row.key]: value }));
  };

  const onSubmit = e => {
    e.preventDefault();
    dispatch(confirmLeaseAgreement());
    if (isKplusAcknowledgementAvailable) {
      dispatch(confirmKplusAcknowledgement());
    }
    segmentEvent('track', 'Lease Agreement Submitted', {
      lease_payment: store.monthlyTotal,
      pay_frequency: store.payFrequency,
    });
    history.push('/checkout');
  };

  return (
    <div className="k-content-wrapper">
      <ErrorHandling error={firstError} />
      <section className="k-info k-info-bottom">
        <h1>Review and acknowledge</h1>
        <p>
          Please select and acknowledge each agreement to continue. Any optional plans will be confirmed at checkout.
        </p>
      </section>
      <section className="k-agreement-checklist">
        <div className="k-agreement-checklist__progress-row">
          <span>
            {acknowledgedCount} of {rows.length} acknowledged
          </span>
          <span>{progressPercent}%</span>
        </div>
        <div className="k-agreement-checklist__progress-track">
          <div className="k-agreement-checklist__progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
        {rows.map(row => (
          <AgreementChecklistItem
            key={row.key}
            index={row.index}
            title={row.title}
            contractHtml={row.contractHtml}
            loading={row.loading}
            isAcknowledged={acknowledged[row.key]}
            defaultOpen={row.key === 'lease'}
            requireScrollToAcknowledge
            onAcknowledge={value => handleRowAcknowledge(row, value)}
          />
        ))}
      </section>
      <section className="k-form k-form-indent">
        <form onSubmit={onSubmit}>
          {!allAcknowledged && (
            <div className="k-agreement-checklist__remaining">
              {remainingCount} agreement{remainingCount === 1 ? '' : 's'} remaining
            </div>
          )}
          {kplusEnrollError && <div className="k-warning-section">{kplusEnrollError}</div>}
          {boostEnrollError && <div className="k-warning-section">{boostEnrollError}</div>}
          <SubmitButton
            grey={!allAcknowledged}
            disabled={!allAcknowledged}
            text={allAcknowledged ? 'Continue to Payment' : 'Acknowledge All to Continue'}
          />
        </form>
      </section>
    </div>
  );
}

export default LeaseAgreement;
